import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import InlineCitation from './InlineCitation';
import UniversalCitationCard from './UniversalCitationCard';
import StylishViewFileDialog from '../DesignSystems/New/StylishViewFileDialog';
import PratusSelect, { OptionType } from '../DesignSystems/New/PratusSelect';
import {
  getSourceTypeLabel,
  isMapLayerSource,
  getMapFocusFromSource,
  getCustomSourceUrl,
} from './citationUtils';
import type { Geometry } from 'geojson';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { getExtentFromGeoJson } from '../MapPage/CorporateViewTabs/LayersTab/utils/getExtentFromGeoJson';
import { getEntityTypePluralLabel } from './incidentEntityComponents';
import {
  buildEntityUrl,
  getEntityRoutingConfig,
} from '../../utils/buildEntityUrl';
import { getBaseUrlFromLink, getExternalHttpUrl } from '../../utils/urlHelpers';
import { useICSContext } from '../IAP/planningView/ICSContext';

import {
  incidentQueryOptions,
  periodsQueryOptions,
} from '../IAP/hooks/incidentHooks';
import 'styles/components/_citations.scss';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';

// Universal Citation types (matches backend types)
interface UniversalCitation {
  type: 'universal_citation';
  citationId: string;
  citationNumber: number;
  sourceIds: string[];
  sources: SourceAnnotation[];
  startIndex: number;
  endIndex: number;
  citedText?: string;
}

interface SourceAnnotation {
  sourceId: string;
  sourceType: string;
  metadata: any;
  originalContent?: string;
}

type CitationAnnotation = UniversalCitation;

interface SourceInfo {
  sourceType: string;
  sourceName: string;
  sourceId: string;
  citationNumbers: number[];
  sources: SourceAnnotation[];
  fileId?: string;
  pages?: number[];
}

interface AnnotationRendererProps {
  content: string;
  annotations?: CitationAnnotation[]; // Now accepts both FileCitationAnnotation and UniversalCitation
  onFetchFileUrl: (
    fileId: string
  ) => Promise<{ url: string; fileName: string; mimetype: string }>;
  /**
   * Index of the chat message this renderer belongs to, used for
   * scroll-to-message navigation from citation cards.
   */
  messageIndex?: number;
}

type ModalState = {
  show: boolean;
  fileUrl: string;
  fileName: string;
  mimetype: string;
  page?: number;
};

const DEFAULT_MODAL_STATE: ModalState = {
  show: false,
  fileUrl: '',
  fileName: '',
  mimetype: '',
};

/** Infer entity type from sourceName to handle "no data" fallback cases */
function inferEntityType(metadata: any): string {
  const sourceName = (metadata.sourceName || '').toLowerCase();

  // Check sourceName for entity type keywords (order matters - more specific first)
  const patterns: [string[], string][] = [
    [['work assignment', 'assignment'], 'work_assignment'],
    [['objective'], 'objective'],
    [['strateg'], 'strategy'],
    [['tactic'], 'tactic'],
    [['action'], 'action'],
    [['resource request'], 'resource_request'],
    [['resource'], 'resource_set'],
    [['hospital'], 'hospital'],
    [['medical transportation', 'ambulance'], 'medical_transportation'],
    [['medical aid', 'aid station'], 'medical_aid_station'],
    [['communication', 'radio'], 'coms'],
    [['calendar', 'event'], 'calendar_event'],
    [['member', 'roster'], 'member'],
    [['strike team'], 'strike_team'],
  ];

  for (const [keywords, entityType] of patterns) {
    if (keywords.some((k) => sourceName.includes(k))) return entityType;
  }

  return metadata.entityType || 'incident_info';
}

type MapFocus = NonNullable<ReturnType<typeof getMapFocusFromSource>>;

export default function AnnotationRenderer({
  content,
  annotations = [],
  onFetchFileUrl,
  messageIndex,
}: AnnotationRendererProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [modalState, setModalState] = useState<ModalState>(DEFAULT_MODAL_STATE);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { setCard, setInitialResponseTab, setTargetPhase } = useICSContext();
  const {
    setIsMapVisible,
    isMapVisible,
    mapView,
    mapViewElementRef,
    zoomToLocation,
  } = useUscgMapContext();

  // Build source map: group citations by source type and category
  const sourceMap = useMemo(() => {
    const map = new Map<string, SourceInfo>();

    annotations.forEach((citation: CitationAnnotation) => {
      (citation.sources ?? []).forEach((source: SourceAnnotation) => {
        // Determine grouping key based on source type
        let key: string;
        let sourceName: string;

        switch (source.sourceType) {
          case 'INCIDENT': {
            // Group by incidentId + entityType (each category is a separate card)
            // Consolidate incident_metadata and incident_info into one "incident_info" category
            let entityType = source.metadata.entityType || 'unknown';
            if (entityType === 'incident_metadata') {
              entityType = 'incident_info';
            }
            key = `INCIDENT_${source.metadata.incidentId}_${entityType}`;
            sourceName = source.metadata.incidentName || 'Unknown Incident';
            break;
          }
          case 'FILES_RAG':
            // Group by file + page (each file+page combination is a separate card)
            key = `FILES_RAG_${source.metadata.fileId}_${source.metadata.page || 0}`;
            sourceName = source.metadata.sourceName || 'Unknown File';
            break;
          case 'WEB':
            // Group by URL
            key = `WEB_${source.metadata.url || source.sourceId}`;
            sourceName =
              source.metadata.sourceName || source.metadata.url || 'Web Source';
            break;
          default:
            // Default: group by sourceId
            key = `${source.sourceType}_${source.sourceId}`;
            sourceName = source.metadata.sourceName || 'Unknown Source';
        }

        let sourceInfo = map.get(key);
        if (!sourceInfo) {
          sourceInfo = {
            sourceType: source.sourceType,
            sourceName,
            sourceId: key,
            citationNumbers: [],
            sources: [],
          };

          // Add file-specific fields for backward compatibility
          if (source.sourceType === 'FILES_RAG') {
            sourceInfo.fileId = source.metadata.fileId;
            sourceInfo.pages = [];
          }

          map.set(key, sourceInfo);
        }

        // Add citation number if not already present
        if (!sourceInfo.citationNumbers.includes(citation.citationNumber)) {
          sourceInfo.citationNumbers.push(citation.citationNumber);
        }

        // Add source if not already present (track all sources for this group)
        if (!sourceInfo.sources.find((s) => s.sourceId === source.sourceId)) {
          sourceInfo.sources.push(source);
        }

        // Add page for files
        if (
          source.sourceType === 'FILES_RAG' &&
          source.metadata.page &&
          sourceInfo.pages &&
          !sourceInfo.pages.includes(source.metadata.page)
        ) {
          sourceInfo.pages.push(source.metadata.page);
        }
      });
    });

    return map;
  }, [annotations]);

  // Group sourceMap entries into dropdown categories. Most source types collapse
  // under their type label (Files, Web, ...), but INCIDENT splits one level
  // deeper by entity type so the user can target e.g. "Incident - Objectives"
  // separately from "Incident - Tactics".
  const INCIDENT_PREFIX = 'INCIDENT__';

  const categoryMap = useMemo(() => {
    const map = new Map<string, SourceInfo[]>();
    sourceMap.forEach((sourceInfo) => {
      let key = sourceInfo.sourceType;
      if (sourceInfo.sourceType === 'INCIDENT') {
        const rawEntity =
          sourceInfo.sources[0]?.metadata?.entityType || 'incident_info';
        const entity =
          rawEntity === 'incident_metadata' ? 'incident_info' : rawEntity;
        key = `${INCIDENT_PREFIX}${entity}`;
      }
      const list = map.get(key) ?? [];
      list.push(sourceInfo);
      map.set(key, list);
    });
    return map;
  }, [sourceMap]);

  const categoryOptions: OptionType[] = useMemo(
    () =>
      Array.from(categoryMap.entries()).map(([key, items]) => {
        let label: string;
        if (key.startsWith(INCIDENT_PREFIX)) {
          const entityType = key.slice(INCIDENT_PREFIX.length);
          label = `Incident - ${getEntityTypePluralLabel(entityType)} (${items.length})`;
        } else {
          label = `${getSourceTypeLabel(key)} (${items.length})`;
        }
        return { value: key, label };
      }),
    [categoryMap]
  );

  const selectedCategoryOption =
    categoryOptions.find((o) => o.value === selectedCategory) ?? null;

  // Build citation map for fast lookup: 1 -> UniversalCitation
  const citationMap = useMemo(() => {
    const map = new Map<number, UniversalCitation>();

    annotations.forEach((citation) => {
      if (citation.citationNumber) {
        map.set(citation.citationNumber, citation);
      }
    });

    return map;
  }, [annotations]);

  const scrollToOriginParagraph = (citationNumber: number) => {
    if (messageIndex == null) return;
    const message = document.getElementById(`dpgt-message-${messageIndex}`);
    if (!message) return;
    const citationEl = message.querySelector<HTMLElement>(
      `[data-cite="${citationNumber}"]`
    );
    // Prefer the nearest block-level container so the highlight maps to the
    // paragraph (or list item / table cell) the citation lives in, not the
    // whole message.
    const target =
      (citationEl?.closest(
        'p, li, blockquote, td, h1, h2, h3, h4, h5, h6'
      ) as HTMLElement | null) ?? message;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    target.classList.add('citation-target-flash');
    window.setTimeout(
      () => target.classList.remove('citation-target-flash'),
      1200
    );
  };

  const handleCitationClick = async (fileId: string, page?: number) => {
    try {
      const { url, fileName, mimetype } = await onFetchFileUrl(fileId);

      setModalState({
        show: true,
        fileUrl: url,
        fileName,
        mimetype,
        page: page || 1,
      });
    } catch (error) {
      console.error('Failed to fetch file URL:', error);
      toast.error('Failed to open document. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setModalState(DEFAULT_MODAL_STATE);
  };

  const handleIncidentCitationClick = async (metadata: any) => {
    const incidentId = metadata.incidentId;
    // Infer entity type from sourceName to handle "no data" fallback cases
    // where the AI cites fallback data but we want to route to where user can ADD data
    const entityType = inferEntityType(metadata);
    const entityId = metadata.entityId || '';

    if (!incidentId) {
      toast.error('Unable to navigate: missing incident information');
      return;
    }

    try {
      // Use query option factories for consistency with existing hooks
      const incident = await queryClient.fetchQuery(
        incidentQueryOptions(incidentId)
      );
      const periods = await queryClient.fetchQuery(
        periodsQueryOptions(incidentId)
      );

      const currentPeriod = periods.find(
        (p) => p.id === incident.current_period
      );
      const periodNumber = currentPeriod?.period_number ?? 0;

      // Get routing config for this entity type
      const routingConfig = getEntityRoutingConfig(entityType);

      // Set context based on response type for widget/tab/phase selection
      if (periodNumber === 0) {
        // Initial response - set tab for InitialResponsePage
        setInitialResponseTab(routingConfig.initialResponseTab);
      } else {
        // Escalated response - set phase and widget for PlanningDashboard
        setTargetPhase(routingConfig.escalatedPhase);
        setCard(routingConfig.escalatedWidget);
      }

      // Build URL with correct period info and navigate
      const { url } = buildEntityUrl({
        incidentId,
        entityType,
        entityId,
        periodNumber,
      });

      // Hide map before navigating so dashboard content is visible
      setIsMapVisible(false);
      navigate(url);
    } catch (error) {
      console.error('Failed to fetch incident info for navigation:', error);
      // Fallback to basic navigation without context
      setIsMapVisible(false);
      navigate(`/iap/${incidentId}/dashboard`);
    }
  };

  const handleExerciseCitationClick = (metadata: any) => {
    const exerciseId = metadata.exerciseId;
    if (exerciseId) {
      navigate(`/prepare/${exerciseId}/`);
    } else {
      toast.info('Exercise details not available');
    }
  };

  // Point → zoom; anything else → fit the envelope.
  const flyToMapFocus = useCallback(
    (focus: MapFocus) => {
      if (focus.type === 'Point') {
        const [longitude, latitude] = focus.coordinates as [number, number];
        zoomToLocation({ longitude, latitude });
        return;
      }
      const extent = getExtentFromGeoJson(focus as Geometry);
      if (extent) {
        mapView?.goTo(extent, { duration: 500 });
      }
    },
    [mapView, zoomToLocation]
  );

  // The MapView is created once and idles on an offscreen bench until a map
  // page's UscgMapContainer attaches it to that page's viewport. isMapVisible
  // starts out true before any page has attached it, so "on screen" also needs
  // the view to be in the viewport.
  // Reads mapView.container first, unconditionally: reactiveUtils re-runs a
  // predicate only when a reactive property it read changes.
  const isMapAttached = useCallback(() => {
    const container = mapView?.container;
    return !!container && container === mapViewElementRef.current;
  }, [mapView, mapViewElementRef]);

  // A citation clicked while no map is on screen: attaching the view resets its
  // viewpoint, so fly only once it is attached and has settled.
  const [pendingMapFocus, setPendingMapFocus] = useState<MapFocus | null>(null);
  useEffect(() => {
    if (!pendingMapFocus || !mapView) return;
    const controller = new AbortController();
    reactiveUtils
      .whenOnce(() => isMapAttached(), controller.signal)
      .then(() => mapView.when())
      .then(() =>
        reactiveUtils.whenOnce(() => !mapView.updating, controller.signal)
      )
      .then(() => {
        flyToMapFocus(pendingMapFocus);
        setPendingMapFocus(null);
      })
      .catch(() => {
        /* aborted: a newer click or an unmount superseded this fly-to */
      });
    return () => controller.abort();
  }, [pendingMapFocus, mapView, isMapAttached, flyToMapFocus]);

  /**
   * Map-layer citations fly the map to the cited data. The payload carries a
   * canonical `map_focus` GeoJSON (Point or envelope Polygon). If the map is on
   * screen (COP page or an IAP dashboard's map view) we only fire goTo — no
   * navigation, so the user's current page and panel state survive. Otherwise
   * we open the COP map and the fly-to waits for it (see pendingMapFocus).
   */
  const handleMapLayerCitationClick = (source: SourceAnnotation) => {
    const focus = getMapFocusFromSource(source);
    if (!focus) {
      toast.info(
        'This source has no map location to fly to — the cited layers have no plotted features.'
      );
      return;
    }

    if (isMapVisible && isMapAttached()) {
      flyToMapFocus(focus);
      return;
    }
    setIsMapVisible(true);
    navigate('/hub');
    setPendingMapFocus(focus);
  };

  /**
   * Pre-process content to replace citation markers [1], [2] with HTML span
   * elements. This approach works with ReactMarkdown v10+ which removed support
   * for text component overrides. The rehypeRaw plugin preserves these HTML
   * elements, and the span component override renders them as InlineCitation.
   */
  const preprocessContentWithCitations = (text: string): string => {
    if (!citationMap.size) {
      return text;
    }

    // Match both [1] and ([1]) patterns
    const citationRegex = /\(?\[(\d+)\]\)?/g;

    return text.replace(citationRegex, (fullMatch, citNum) => {
      // Check if this citation exists in our annotations
      const citation = citationMap.get(parseInt(citNum, 10));

      if (citation) {
        // Get primary source info (first source)
        const primarySource = citation.sources?.[0];
        const sourceType = primarySource?.sourceType || 'unknown';
        const sourceName = (primarySource?.metadata?.sourceName || 'Unknown')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');

        // For file sources, include file-specific data
        let dataAttrs = `data-cite="${citNum}" data-source-type="${sourceType}" data-source-name="${sourceName}"`;

        if (sourceType === 'FILES_RAG') {
          const page = primarySource.metadata?.page || '';
          const fileId = primarySource.metadata?.fileId || '';
          dataAttrs += ` data-page="${page}" data-file-id="${fileId}"`;
        }

        return `<span class="citation-placeholder" ${dataAttrs}>[${citNum}]</span>`;
      }

      // Citation not found in annotations, keep as plain text
      return fullMatch;
    });
  };

  // Custom components for ReactMarkdown
  const markdownComponents = {
    // Override anchor rendering to open external links in new tab
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    a(props: any) {
      const { node: _node, children, href, ...rest } = props;

      // Check if this is an external link
      const isExternal =
        href &&
        (href.startsWith('http://') ||
          href.startsWith('https://') ||
          href.startsWith('//'));

      if (isExternal) {
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
            {children}
          </a>
        );
      }

      // Internal link, render normally
      return (
        <a href={href} {...rest}>
          {children}
        </a>
      );
    },

    // Override span rendering to convert citation placeholders to InlineCitation components
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    span(props: any) {
      const { node: _node, children, className, ...rest } = props;

      // Check if this is a citation placeholder span
      if (className === 'citation-placeholder' && rest['data-cite']) {
        const citNum = parseInt(rest['data-cite'], 10);
        const citation = citationMap.get(citNum);

        if (citation) {
          const primarySource = citation.sources?.[0];
          const sourceType = primarySource?.sourceType || 'unknown';
          const sourceName = primarySource?.metadata?.sourceName || 'Unknown';

          // Handle file citations
          if (sourceType === 'FILES_RAG') {
            return (
              <InlineCitation
                citationNumber={citNum}
                page={primarySource.metadata?.page}
                fileName={sourceName}
                sourceType={sourceType}
                onClick={() =>
                  handleCitationClick(
                    primarySource.metadata?.fileId,
                    primarySource.metadata?.page
                  )
                }
              />
            );
          }

          // Handle Incident citations - open the viewer
          if (sourceType === 'INCIDENT') {
            return (
              <InlineCitation
                citationNumber={citNum}
                fileName={sourceName}
                sourceType={sourceType}
                incidentName={primarySource.metadata?.incidentName}
                entityType={primarySource.metadata?.entityType}
                onClick={() =>
                  handleIncidentCitationClick(primarySource.metadata)
                }
              />
            );
          }

          // Handle EXERCISE citations - navigate to exercise dashboard
          if (sourceType === 'EXERCISE') {
            return (
              <InlineCitation
                citationNumber={citNum}
                fileName={sourceName}
                sourceType={sourceType}
                onClick={() =>
                  handleExerciseCitationClick(primarySource.metadata)
                }
              />
            );
          }

          // Handle WEB citations - open URL in new tab
          if (sourceType === 'WEB') {
            return (
              <InlineCitation
                citationNumber={citNum}
                fileName={sourceName}
                sourceType={sourceType}
                baseUrl={
                  getBaseUrlFromLink(primarySource.metadata?.url) || undefined
                }
                onClick={() => {
                  // Only an absolute http(s) URL is opened — anything else
                  // would resolve against the PRATUS origin and open PRATUS
                  // itself in the new tab.
                  const webUrl = getExternalHttpUrl(
                    primarySource.metadata?.url
                  );
                  if (webUrl) {
                    window.open(webUrl, '_blank', 'noopener,noreferrer');
                  } else {
                    toast.info(`Source: ${sourceName}`);
                  }
                }}
              />
            );
          }

          // Handle SITREP citations - navigate to /map
          if (sourceType === 'SITREP') {
            return (
              <InlineCitation
                citationNumber={citNum}
                fileName={sourceName}
                sourceType={sourceType}
                onClick={() => navigate('/hub')}
              />
            );
          }

          // Handle CUSTOM citations - map-layer sources fly the map to the
          // cited data; sources carrying a URL (census, hazard feeds) open it
          // in a new tab; anything else shows the info toast
          if (sourceType === 'CUSTOM') {
            const isMapLayers = isMapLayerSource(primarySource);
            const sourceUrl = getCustomSourceUrl(primarySource);
            return (
              <InlineCitation
                citationNumber={citNum}
                fileName={sourceName}
                sourceType={sourceType}
                onClick={() => {
                  if (isMapLayers) {
                    handleMapLayerCitationClick(primarySource);
                  } else if (sourceUrl) {
                    window.open(sourceUrl, '_blank', 'noopener,noreferrer');
                  } else {
                    toast.info(`Source: ${sourceName}`);
                  }
                }}
              />
            );
          }

          // Default fallback for any other source types
          return (
            <InlineCitation
              citationNumber={citNum}
              fileName={sourceName}
              sourceType={sourceType}
              onClick={() => {
                const sourcesSection =
                  document.querySelector('.sources-section');
                if (sourcesSection) {
                  sourcesSection.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                  });
                }
              }}
            />
          );
        }
      }

      // Regular span, render normally
      return (
        <span className={className} {...rest}>
          {children}
        </span>
      );
    },
  };

  // Render content with citations section below
  const renderContentWithCitations = () => {
    if (!annotations || annotations.length === 0) {
      return <ReactMarkdown>{content}</ReactMarkdown>;
    }

    // Pre-process content to replace citation markers with HTML spans
    const processedContent = preprocessContentWithCitations(content);

    return (
      <div className="message-with-citations">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[[rehypeRaw, { passThrough: ['element'] }]]}
          components={markdownComponents}
        >
          {processedContent}
        </ReactMarkdown>

        {/* Sources section - category dropdown reveals cards for the selected
            source type. Default state has no category selected. */}
        {sourceMap.size > 0 && (
          <div className="sources-section border-top mt-3 pt-3">
            <strong>Sources:</strong>
            <div className="mt-2">
              <PratusSelect
                options={categoryOptions}
                value={selectedCategoryOption}
                onChange={(option) => {
                  const next = option as OptionType | null;
                  setSelectedCategory(next ? String(next.value) : null);
                }}
                isClearable
                placeholder="Select Annotation Category"
                classes="secondary"
              />
            </div>
            {selectedCategory && (
              <div className="mt-2">
                {(categoryMap.get(selectedCategory) ?? []).map((sourceInfo) => {
                  // Build citations array with citation number and source data
                  const citations = sourceInfo.citationNumbers.map((citNum) => {
                    const citation = citationMap.get(citNum);
                    // Find the source from this citation that belongs to this group
                    const source =
                      citation?.sources?.find(
                        (s) =>
                          s.sourceType === sourceInfo.sourceType &&
                          sourceInfo.sources.some(
                            (gs) => gs.sourceId === s.sourceId
                          )
                      ) || sourceInfo.sources[0];

                    return {
                      number: citNum,
                      source,
                    };
                  });

                  return (
                    <UniversalCitationCard
                      key={sourceInfo.sourceId}
                      sourceType={sourceInfo.sourceType}
                      sourceName={sourceInfo.sourceName}
                      citations={citations}
                      onFileCitationClick={handleCitationClick}
                      onIncidentCitationClick={handleIncidentCitationClick}
                      onExerciseCitationClick={handleExerciseCitationClick}
                      onSitrepCitationClick={() => navigate('/hub')}
                      onMapLayerCitationClick={handleMapLayerCitationClick}
                      onScrollToSource={
                        messageIndex != null
                          ? () =>
                              scrollToOriginParagraph(
                                sourceInfo.citationNumbers[0]
                              )
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {renderContentWithCitations()}

      {/* Unified document viewer for all file types */}
      {modalState.show && (
        <StylishViewFileDialog
          show={modalState.show}
          onClose={handleCloseModal}
          fileUrl={modalState.fileUrl}
          fileName={modalState.fileName}
          page={modalState.page}
          mimetype={modalState.mimetype}
        />
      )}
    </>
  );
}
